import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { Card, Button, Spinner, Badge, Modal } from '../../components/UI';
import { integrationsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineLink, HiOutlineExternalLink, HiOutlineRefresh, HiOutlineClipboardCopy,
  HiOutlineCheck, HiOutlineX, HiOutlineExclamation, HiOutlineCog,
  HiOutlineDocumentText, HiOutlineChartBar, HiOutlinePlus, HiOutlineTrash,
  HiOutlineShieldCheck, HiOutlineEye, HiOutlineEyeOff, HiOutlineKey
} from 'react-icons/hi';

// Map integration type ID to the credential key name expected by the backend schema
const CREDENTIAL_KEY_MAP = {
  indiamart: 'indiamart',
  justdial: 'justdial',
  meta_lead_ads: 'meta',
  custom_webhook: null
};

// Credentials that should be rendered as password fields (sensitive)
const SECRET_CREDENTIALS = new Set(['authKey', 'clientSecret', 'appSecret']);

// Integration type configurations
const INTEGRATION_TYPES = [
  {
    id: 'indiamart',
    name: 'IndiaMART',
    description: 'Import leads from IndiaMART via webhook',
    icon: '🛒',
    color: '#FF5722',
    credentials: ['crmId', 'authKey'],
    optionalCredentials: [],
    credentialsLabels: { crmId: 'CRM ID', authKey: 'Auth Key' }
  },
  {
    id: 'justdial',
    name: 'JustDial',
    description: 'Import leads from JustDial via webhook',
    icon: '📞',
    color: '#4CAF50',
    credentials: ['clientId', 'clientSecret'],
    optionalCredentials: [],
    credentialsLabels: { clientId: 'Client ID', clientSecret: 'Client Secret' }
  },
  {
    id: 'meta_lead_ads',
    name: 'Meta Lead Ads',
    description: 'Import leads from Facebook/Instagram Lead Ads',
    icon: '📘',
    color: '#1877F2',
    credentials: ['appId', 'appSecret'],
    optionalCredentials: ['pageId'],
    credentialsLabels: { appId: 'App ID', appSecret: 'App Secret', pageId: 'Page ID (optional)' }
  },
  {
    id: 'custom_webhook',
    name: 'Custom Webhook',
    description: 'Receive leads from any custom webhook',
    icon: '🔗',
    color: '#9C27B0',
    credentials: [],
    credentialsLabels: {}
  }
];

// Webhook payload documentation for each integration type
const WEBHOOK_PAYLOAD_DOCS = {
  custom_webhook: {
    title: 'Custom Webhook',
    description: 'Send lead data as JSON to your webhook endpoint. You can send a single lead object or an array wrapped in a "leads", "data", "contacts", or "customers" key.',
    requiredFields: ['phone'],
    optionalFields: ['name', 'email', 'company', 'notes', 'address', 'city', 'state', 'country'],
    samplePayload: {
      name: 'John Doe',
      phone: '9876543210',
      email: 'john@example.com',
      company: 'Acme Corp',
      notes: 'Interested in product X',
      address: '123 Main St',
      city: 'Mumbai',
      state: 'Maharashtra',
      country: 'India'
    },
    wrapperFormats: ['leads', 'data', 'contacts', 'customers'],
    notes: 'Phone number must be 7-15 digits. International format with + prefix (e.g., +971501234567, +14155551234) or local format (e.g., 9876543210) are both accepted. You can also send an array directly at the top level.',
    signatureHeader: 'x-webhook-signature'
  },
  indiamart: {
    title: 'IndiaMART',
    description: 'IndiaMART sends leads via their API. The payload can arrive in several formats depending on your IndiaMART plan.',
    requiredFields: ['MOBILE or phone'],
    optionalFields: ['NAME or name', 'EMAIL or email', 'COMPANYNAME or company', 'QUERY or message', 'CITY or city', 'STATE or state', 'COUNTRY or country', 'PINCODE or pincode'],
    samplePayload: {
      LEAD: {
        NAME: 'John Doe',
        MOBILE: '9876543210',
        EMAIL: 'john@example.com',
        COMPANYNAME: 'Acme Corp',
        QUERY: 'Interested in product X',
        CITY: 'Mumbai',
        STATE: 'Maharashtra',
        COUNTRY: 'India',
        PINCODE: '400001'
      }
    },
    wrapperFormats: ['LEAD', 'leads', 'data', 'enquiries'],
    notes: 'IndiaMART may also send leads inside "leads", "data", or "enquiries" arrays. Phone field can be "MOBILE", "phone", "sender_mobile", or "Lead_Mobile". The system automatically maps common field names.',
    signatureHeader: 'x-indiamart-signature'
  },
  justdial: {
    title: 'JustDial',
    description: 'JustDial sends leads via their webhook integration. Field names are typically lowercase.',
    requiredFields: ['phone or mobile'],
    optionalFields: ['name or customer_name', 'email or customer_email', 'company or business_name', 'message or enquiry', 'city or City', 'state or State', 'address or Location'],
    samplePayload: {
      name: 'John Doe',
      phone: '9876543210',
      email: 'john@example.com',
      company: 'Acme Corp',
      message: 'Interested in product X',
      city: 'Mumbai',
      state: 'Maharashtra'
    },
    wrapperFormats: ['leads', 'data', 'enquiries', 'results'],
    notes: 'JustDial typically uses lowercase field names. The system also recognizes "Caller_Name", "Phone_Number", "Contact_No", and "Firm_Name" variants.',
    signatureHeader: 'x-justdial-signature'
  },
  meta_lead_ads: {
    title: 'Meta Lead Ads',
    description: 'Facebook/Instagram Lead Ads follow the standard Meta Webhooks format with entry/changes structure.',
    requiredFields: ['phone_number (in field_data)'],
    optionalFields: ['full_name or first_name + last_name', 'email or email_address', 'company_name', 'message or comments', 'page_id', 'form_id', 'leadgen_id'],
    samplePayload: {
      entry: [
        {
          changes: [
            {
              field: 'leadgen',
              value: {
                leadgen_id: '1234567890',
                form_id: '9876543210',
                page_id: '5551234567',
                field_data: [
                  { name: 'full_name', values: ['John Doe'] },
                  { name: 'phone_number', values: ['9876543210'] },
                  { name: 'email', values: ['john@example.com'] }
                ]
              }
            }
          ]
        }
      ]
    },
    wrapperFormats: [],
    notes: 'Meta also requires a GET verification endpoint. When you configure the webhook in Meta, they send a GET request with hub.mode=subscribe and hub.challenge parameters. Our server automatically handles this verification using the token generated when you create the integration. The signature header uses the same format as Meta\'s standard webhook verification.',
    signatureHeader: 'x-hub-signature-256'
  },
  website_forms: {
    title: 'Website Forms',
    description: 'Send form submissions from your website as JSON payloads.',
    requiredFields: ['phone or mobile'],
    optionalFields: ['name or full_name', 'email or email_address', 'company or organization', 'message or query', 'city or City', 'state or State', 'country or Country', 'address or Address'],
    samplePayload: {
      name: 'John Doe',
      phone: '9876543210',
      email: 'john@example.com',
      company: 'Acme Corp',
      message: 'Interested in product X',
      city: 'Mumbai',
      state: 'Maharashtra'
    },
    wrapperFormats: ['leads', 'data', 'contacts'],
    notes: 'You can send a single lead object or wrap multiple leads in a "leads", "data", or "contacts" array. The source defaults to "Website Form" unless overridden in integration settings.',
    signatureHeader: 'x-webhook-signature'
  }
};

// Payload Documentation Component
const PayloadDocs = ({ integration }) => {
  const [showPayload, setShowPayload] = useState(false);
  const [copiedField, setCopiedField] = useState('');
  const [sigTab, setSigTab] = useState('simple'); // 'simple' | 'nodejs' | 'python' | 'curl' | 'postman'

  const docs = WEBHOOK_PAYLOAD_DOCS[integration.type];
  if (!docs) return null;

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedField(label);
    setTimeout(() => setCopiedField(''), 2000);
  };

  const sampleJson = JSON.stringify(docs.samplePayload, null, 2);

  // Simplest method — send the secret key itself (no signature code needed)
  const hookUrl = integration.webhookUrl || '<your_webhook_url>';
  const urlWithSecret = `${hookUrl}${hookUrl.includes('?') ? '&' : '?'}secret=<your_secret_key>`;
  const simpleCurl = `curl -X POST "${hookUrl}" \
  -H "Content-Type: application/json" \
  -H "x-webhook-secret: <your_secret_key>" \
  -d '${JSON.stringify(docs.samplePayload)}'`;

  // Generate curl example with actual webhook URL (HMAC signature)
  const curlExample = integration.webhookUrl
    ? `curl -X POST "${integration.webhookUrl}" \\
  -H "Content-Type: application/json" \\
  -H "x-webhook-signature: sha256=<computed_signature>" \\
  -d '${JSON.stringify(docs.samplePayload)}'`
    : `curl -X POST "<your_webhook_url>" \\
  -H "Content-Type: application/json" \\
  -H "x-webhook-signature: sha256=<computed_signature>" \\
  -d '${JSON.stringify(docs.samplePayload)}'`;

  const nodejsCode = `const crypto = require('crypto');

const secretKey = '<your_signing_secret>';
const body = JSON.stringify(${JSON.stringify(docs.samplePayload)});

const signature = 'sha256=' + crypto
  .createHmac('sha256', secretKey)
  .update(body)
  .digest('hex');

// Include in request headers:
// 'x-webhook-signature': signature`;

  const pythonCode = `import hmac, hashlib, json

secret_key = b'<your_signing_secret>'
body = json.dumps(${JSON.stringify(docs.samplePayload)}, separators=(',', ':'))

signature = 'sha256=' + hmac.new(
    secret_key,
    body.encode(),
    hashlib.sha256
).hexdigest()

# Include in request headers:
# 'x-webhook-signature': signature`;

  // Ready-to-paste Postman program. IMPORTANT: the secret key goes in the
  // Pre-request Script (as the HMAC key), NOT in the x-webhook-signature
  // header — the header receives the computed signature.
  const postmanCode = `// ── Postman Setup ─────────────────────────────────────────────
// Method:  POST
// URL:     ${integration.webhookUrl || '<your_webhook_url>'}
// Body:    raw → JSON → paste the Sample JSON Payload above
// Headers: Content-Type: application/json
//          (do NOT add x-webhook-signature manually — the
//           pre-request script below adds it on every send)
//
// ── Scripts → Pre-request ─────────────────────────────────────
const secret = '<your_signing_secret>'; // secret key = HMAC key, never the header value

const body = (typeof request.data === 'string')
  ? request.data                      // exact raw body text being sent
  : JSON.stringify(request.data);

const signature = CryptoJS.HmacSHA256(body, secret).toString(CryptoJS.enc.Hex);

pm.request.headers.upsert({ key: 'x-webhook-signature', value: signature });

// ── Scripts → Post-response (Tests) ───────────────────────────
pm.test("Status code is 200", function () {
    pm.response.to.have.status(200);
});

pm.test("Clear diagnosis on 401/404", function () {
    if (pm.response.code === 401) {
        throw new Error('401: ' + (pm.response.json().message || 'bad signature'));
    }
    if (pm.response.code === 404) {
        throw new Error('404: endpoint ID in URL is wrong');
    }
});

pm.test("Webhook processed", function () {
    pm.expect(pm.response.json().success).to.eql(true);
});`;

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setShowPayload(!showPayload)}
        className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
      >
        <HiOutlineDocumentText className="w-4 h-4" />
        {showPayload ? 'Hide Data Format' : 'View Data Format'}
      </button>

      {showPayload && (
        <div className="mt-3 space-y-4">
          {/* Description */}
          <div className="text-xs text-gray-600">
            {docs.description}
          </div>

          {/* Required / Optional Fields */}
          <div className="grid grid-cols-2 gap-3">
            {docs.requiredFields.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-red-700 mb-1.5">Required Fields</p>
                <div className="space-y-1">
                  {docs.requiredFields.map((field) => (
                    <div key={field} className="flex items-center gap-1.5 text-xs text-red-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                      <code className="bg-red-100 px-1 rounded">{field}</code>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {docs.optionalFields.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-blue-700 mb-1.5">Optional Fields</p>
                <div className="space-y-1">
                  {docs.optionalFields.slice(0, 8).map((field) => (
                    <div key={field} className="flex items-center gap-1.5 text-xs text-blue-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                      <code className="bg-blue-100 px-1 rounded">{field}</code>
                    </div>
                  ))}
                  {docs.optionalFields.length > 8 && (
                    <p className="text-xs text-blue-400 italic">+{docs.optionalFields.length - 8} more</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Wrapper Formats */}
          {docs.wrapperFormats && docs.wrapperFormats.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-xs font-medium text-gray-700 mb-1">Array Wrappers</p>
              <p className="text-xs text-gray-500 mb-1.5">
                You can also send multiple leads by wrapping them in one of these keys:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {docs.wrapperFormats.map((key) => (
                  <code key={key} className="text-xs bg-white border border-gray-300 px-2 py-0.5 rounded">
                    {"{ \""}{key}{"\": [...] }"}
                  </code>
                ))}
              </div>
            </div>
          )}

          {/* Sample JSON Payload */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-gray-700">Sample JSON Payload</p>
              <button
                onClick={() => copyToClipboard(sampleJson, 'payload')}
                className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                {copiedField === 'payload' ? (
                  <><HiOutlineCheck className="w-3 h-3" /> Copied</>
                ) : (
                  <><HiOutlineClipboardCopy className="w-3 h-3" /> Copy</>
                )}
              </button>
            </div>
            <pre className="text-xs bg-gray-900 text-green-300 p-3 rounded-lg overflow-x-auto max-h-64 overflow-y-auto leading-relaxed">
              {sampleJson}
            </pre>
          </div>

          {/* Notes */}
          {docs.notes && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <HiOutlineExclamation className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">{docs.notes}</p>
              </div>
            </div>
          )}

          {/* Authentication — any ONE of three methods */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2">
            <p className="text-xs font-semibold text-purple-700">Authentication — use ANY ONE of these</p>
            <p className="text-xs text-purple-600">
              Replace <code className="bg-purple-100 px-1 rounded">&lt;your_secret_key&gt;</code> with this
              integration&apos;s <strong>Secret Key</strong> (shown below — reveal &amp; copy it once, or regenerate it).
            </p>
            <div className="text-xs text-gray-700 space-y-2">
              <div>
                <p className="font-semibold">1. Secret in the URL — easiest (IndiaMART, JustDial, website forms, any tool that only takes a URL)</p>
                <div className="flex items-start gap-2 mt-1">
                  <code className="flex-1 min-w-0 break-all bg-white border px-1.5 py-1 rounded">{urlWithSecret}</code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(urlWithSecret, 'urlsecret')}
                    className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 flex-shrink-0"
                  >
                    {copiedField === 'urlsecret' ? <><HiOutlineCheck className="w-3 h-3" /> Copied</> : <><HiOutlineClipboardCopy className="w-3 h-3" /> Copy</>}
                  </button>
                </div>
              </div>
              <div>
                <p className="font-semibold">2. Secret in a header (Zapier, Pabbly, Make, Postman, your server)</p>
                <p className="mt-1">
                  <code className="bg-white border px-1.5 py-0.5 rounded">x-webhook-secret: &lt;your_secret_key&gt;</code>
                  {' '}or{' '}
                  <code className="bg-white border px-1.5 py-0.5 rounded">Authorization: Bearer &lt;your_secret_key&gt;</code>
                </p>
              </div>
              <div>
                <p className="font-semibold">3. HMAC signature — most secure (for developers)</p>
                <p className="mt-1">
                  Header <code className="bg-white border px-1 rounded">{docs.signatureHeader}</code> ={' '}
                  <code className="bg-white border px-1 rounded">sha256=&lt;HMAC-SHA256(exact request body, secret key)&gt;</code>
                  {' '}— see the Node.js / Python / Postman tabs.
                </p>
              </div>
            </div>
          </div>

          {/* Signature Generation Code — tabs for Node.js / Python */}
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-1">Examples</p>
            <div className="flex flex-wrap gap-1 mb-2">
              <button
                onClick={() => setSigTab('simple')}
                className={`text-xs px-2.5 py-1 rounded font-medium transition-colors ${
                  sigTab === 'simple'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                cURL (secret header)
              </button>
              <button
                onClick={() => setSigTab('nodejs')}
                className={`text-xs px-2.5 py-1 rounded font-medium transition-colors ${
                  sigTab === 'nodejs'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Node.js
              </button>
              <button
                onClick={() => setSigTab('python')}
                className={`text-xs px-2.5 py-1 rounded font-medium transition-colors ${
                  sigTab === 'python'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Python
              </button>
              <button
                onClick={() => setSigTab('curl')}
                className={`text-xs px-2.5 py-1 rounded font-medium transition-colors ${
                  sigTab === 'curl'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                cURL (signature)
              </button>
              <button
                onClick={() => setSigTab('postman')}
                className={`text-xs px-2.5 py-1 rounded font-medium transition-colors ${
                  sigTab === 'postman'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Postman
              </button>
            </div>
            <div className="relative">
              <button
                onClick={() => copyToClipboard(
                  sigTab === 'simple' ? simpleCurl : sigTab === 'nodejs' ? nodejsCode : sigTab === 'python' ? pythonCode : sigTab === 'postman' ? postmanCode : curlExample,
                  `sig_${sigTab}`
                )}
                className="absolute top-2 right-2 text-xs text-gray-400 hover:text-gray-200 flex items-center gap-1 z-10"
              >
                {copiedField === `sig_${sigTab}` ? (
                  <><HiOutlineCheck className="w-3 h-3" /> Copied</>
                ) : (
                  <><HiOutlineClipboardCopy className="w-3 h-3" /> Copy</>
                )}
              </button>
              <pre className="text-xs bg-gray-900 text-green-300 p-3 rounded-lg overflow-x-auto max-h-64 overflow-y-auto leading-relaxed">
                {sigTab === 'simple' ? simpleCurl : sigTab === 'nodejs' ? nodejsCode : sigTab === 'python' ? pythonCode : sigTab === 'postman' ? postmanCode : curlExample}
              </pre>
            </div>
          </div>

          {/* Response Format */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-700 mb-1">Expected Response</p>
            <pre className="text-xs text-gray-600 leading-relaxed">
{`{
  "success": true,
  "message": "Processed 1 lead(s) successfully",
  "data": {
    "received": 1,
    "imported": 1,
    "duplicates": 0,
    "errors": 0,
    "logId": "..."
  }
}`}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusStyles = {
    active: 'bg-green-100 text-green-700',
    pending_verification: 'bg-amber-100 text-amber-700 border border-amber-300',
    paused: 'bg-gray-100 text-gray-700',
    error: 'bg-red-100 text-red-700',
    disabled: 'bg-gray-100 text-gray-500'
  };

  const statusLabels = {
    active: 'Active',
    pending_verification: '⚠ Needs Activation',
    paused: 'Paused',
    error: 'Error',
    disabled: 'Disabled'
  };

  return (
    <Badge className={statusStyles[status] || statusStyles.paused}>
      {statusLabels[status] || status}
    </Badge>
  );
};

// Health indicator component
const HealthIndicator = ({ health }) => {
  if (!health) return null;

  const statusColors = {
    healthy: 'bg-green-500',
    degraded: 'bg-yellow-500',
    unhealthy: 'bg-red-500',
    unknown: 'bg-gray-300'
  };

  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-2 h-2 rounded-full ${statusColors[health.status] || statusColors.unknown}`} />
      <span className="text-xs text-gray-500 capitalize">{health.status}</span>
    </div>
  );
};

// Integration Card Component
const IntegrationCard = ({ integration, onConfigure, onToggle, onViewLogs, onTest, onDelete }) => {
  const queryClient = useQueryClient();
  const typeConfig = INTEGRATION_TYPES.find(t => t.id === integration.type) || INTEGRATION_TYPES[3];
  const [copied, setCopied] = React.useState('');
  const [showSecret, setShowSecret] = React.useState(false);
  const [fullSecret, setFullSecret] = React.useState(null);
  const [revealingSecret, setRevealingSecret] = React.useState(false);
  const [regenerating, setRegenerating] = React.useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = React.useState(false);

  const secretAlreadyRevealed = !!integration.webhook?.secretRevealedAt;

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleRevealSecret = async () => {
    if (fullSecret) {
      setShowSecret(!showSecret);
      return;
    }
    try {
      setRevealingSecret(true);
      const response = await integrationsAPI.revealSecret(integration._id);
      const secret = response.data?.data?.secretKey;
      setFullSecret(secret);
      setShowSecret(true);
      // Refetch to update secretRevealedAt
      queryClient.invalidateQueries(['integrations']);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to reveal secret key';
      toast.error(message);
    } finally {
      setRevealingSecret(false);
    }
  };

  const handleDismissSecret = () => {
    setShowSecret(false);
    setFullSecret(null);
    // Refetch to update secretRevealedAt state
    queryClient.invalidateQueries(['integrations']);
  };

  const handleRegenerateSecret = async () => {
    try {
      setRegenerating(true);
      const response = await integrationsAPI.regenerateSecret(integration._id);
      const newSecret = response.data?.data?.secretKey;
      setFullSecret(newSecret);
      setShowSecret(true);
      toast.success('New secret key generated. Copy it now — it will only be shown once.');
      queryClient.invalidateQueries(['integrations']);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to regenerate secret key');
    } finally {
      setRegenerating(false);
      setShowRegenerateConfirm(false);
    }
  };

  return (
    <Card padding="p-5" className="hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
            style={{ backgroundColor: `${typeConfig.color}20` }}
          >
            {typeConfig.icon}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{integration.name || typeConfig.name}</h3>
            <p className="text-sm text-gray-500">{typeConfig.description}</p>
          </div>
        </div>
        <StatusBadge status={integration.status} />
      </div>

      {/* Security Notice */}
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <HiOutlineShieldCheck className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-blue-700">Secured Webhook</p>
            <p className="text-xs text-blue-600 mt-0.5">
              All webhook requests must include a valid HMAC-SHA256 signature in the <code className="bg-blue-100 px-1 rounded">x-webhook-signature</code> header.
            </p>
          </div>
        </div>
      </div>

      {/* Webhook URL */}
      {integration.webhookUrl && (
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-500 mb-1">Webhook URL</label>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-gray-100 px-2 py-1.5 rounded truncate text-gray-700">
              {integration.webhookUrl}
            </code>
            <button
              onClick={() => copyToClipboard(integration.webhookUrl, 'url')}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
              title="Copy webhook URL"
            >
              {copied === 'url' ? (
                <HiOutlineCheck className="w-4 h-4 text-green-600" />
              ) : (
                <HiOutlineClipboardCopy className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Secret Key — One-time reveal */}
      {integration.webhook?.secretKeyMasked && (
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
            <HiOutlineKey className="w-3 h-3" />
            Signing Secret
          </label>

          {/* One-time reveal banner — shown when secret is currently visible */}
          {showSecret && fullSecret ? (
            <div className="space-y-2">
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                <p className="text-xs font-semibold text-amber-800 mb-1">
                  ⚠ Copy this secret now — you won't be able to see it again!
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <code className="flex-1 text-xs bg-white border border-amber-200 px-2 py-1.5 rounded font-mono break-all text-gray-900">
                    {fullSecret}
                  </code>
                  <button
                    onClick={() => copyToClipboard(fullSecret, 'secret')}
                    className="p-1.5 text-amber-700 hover:text-amber-900 hover:bg-amber-100 rounded transition-colors flex-shrink-0"
                    title="Copy secret"
                  >
                    {copied === 'secret' ? (
                      <HiOutlineCheck className="w-4 h-4 text-green-600" />
                    ) : (
                      <HiOutlineClipboardCopy className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <button
                  onClick={handleDismissSecret}
                  className="mt-2 text-xs text-amber-700 hover:text-amber-900 font-medium underline"
                >
                  I've copied it, hide this secret
                </button>
              </div>
            </div>
          ) : secretAlreadyRevealed ? (
            /* Already viewed — show masked + regenerate option */
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-gray-100 px-2 py-1.5 rounded text-gray-500 font-mono">
                  {integration.webhook.secretKeyMasked}
                </code>
                <span className="text-xs text-gray-400 italic">Already viewed</span>
              </div>
              <button
                onClick={() => setShowRegenerateConfirm(true)}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Regenerate Secret
              </button>
            </div>
          ) : (
            /* Never revealed — show reveal button */
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-gray-100 px-2 py-1.5 rounded text-gray-700 font-mono">
                {integration.webhook.secretKeyMasked}
              </code>
              <button
                onClick={handleRevealSecret}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                title="Reveal secret (one-time)"
                disabled={revealingSecret}
              >
                {revealingSecret ? (
                  <Spinner size="sm" />
                ) : (
                  <HiOutlineEye className="w-4 h-4" />
                )}
              </button>
            </div>
          )}

          {/* Regenerate confirmation */}
          {showRegenerateConfirm && (
            <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs font-medium text-red-700">Regenerate signing secret?</p>
              <p className="text-xs text-red-600 mt-1">
                This will invalidate the current secret. Any webhooks using the old secret will stop working until updated.
              </p>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleRegenerateSecret}
                  disabled={regenerating}
                  className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded font-medium disabled:opacity-50"
                >
                  {regenerating ? 'Regenerating...' : 'Yes, regenerate'}
                </button>
                <button
                  onClick={() => setShowRegenerateConfirm(false)}
                  className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!showSecret && !secretAlreadyRevealed && (
            <p className="text-xs text-gray-400 mt-1">
              Can only be revealed once. Use it to generate the HMAC-SHA256 signature.
            </p>
          )}
        </div>
      )}

      {/* Webhook Data Format Documentation */}
      <PayloadDocs integration={integration} />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center p-2 bg-gray-50 rounded">
          <div className="text-lg font-semibold text-gray-900">
            {integration.stats?.totalImported || 0}
          </div>
          <div className="text-xs text-gray-500">Imported</div>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded">
          <div className="text-lg font-semibold text-gray-900">
            {integration.stats?.totalDuplicates || 0}
          </div>
          <div className="text-xs text-gray-500">Duplicates</div>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded">
          <div className="text-lg font-semibold text-gray-900">
            {integration.stats?.totalErrors || 0}
          </div>
          <div className="text-xs text-gray-500">Errors</div>
        </div>
      </div>

      {/* Last Activity */}
      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
        <span>Last synced:</span>
        <span>
          {integration.stats?.lastProcessedAt
            ? new Date(integration.stats.lastProcessedAt).toLocaleString()
            : 'Never'}
        </span>
      </div>

      {/* Health */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-gray-500">Health:</span>
        <HealthIndicator health={integration.health} />
      </div>

      {/* Error Message */}
      {(integration.lastError?.message || integration.health?.errorMessage) && (
        <div className="bg-red-50 border border-red-200 rounded p-2 mb-4">
          <div className="flex items-center gap-1.5 text-red-700 text-sm">
            <HiOutlineExclamation className="w-4 h-4" />
            <span className="font-medium">Last Error:</span>
          </div>
          <p className="text-xs text-red-600 mt-1 truncate">
            {integration.lastError?.message || integration.health?.errorMessage}
          </p>
        </div>
      )}

      {/* Pending Verification Notice */}
      {integration.status === 'pending_verification' && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4">
          <div className="flex items-start gap-2">
            <HiOutlineExclamation className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-amber-700 font-medium">Action Required</p>
              <p className="text-xs text-amber-600 mt-1">
                {typeConfig?.credentials?.length
                  ? `Configure ${typeConfig.name} credentials and click Activate to start receiving leads.`
                  : 'Click Activate to start receiving leads via webhook.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          icon={<HiOutlineCog className="w-4 h-4" />}
          onClick={() => onConfigure(integration)}
        >
          Configure
        </Button>

        {integration.status === 'pending_verification' ? (
          <Button
            variant="outline"
            size="sm"
            className="text-green-600 border-green-300 hover:bg-green-50"
            onClick={() => onToggle(integration, true)}
          >
            Activate
          </Button>
        ) : integration.isActive ? (
          <Button
            variant="outline"
            size="sm"
            className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
            onClick={() => onToggle(integration, false)}
          >
            Disable
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="text-green-600 border-green-300 hover:bg-green-50"
            onClick={() => onToggle(integration, true)}
          >
            Enable
          </Button>
        )}

        <Button
          variant="ghost"
          size="sm"
          icon={<HiOutlineDocumentText className="w-4 h-4" />}
          onClick={() => onViewLogs(integration)}
          title="View Logs"
        >
          Logs
        </Button>

        <Button
          variant="ghost"
          size="sm"
          icon={<HiOutlineRefresh className="w-4 h-4" />}
          onClick={() => onTest(integration)}
          title="Test Connection"
        >
          Test
        </Button>

        <Button
          variant="ghost"
          size="sm"
          className="text-red-500 hover:text-red-700 hover:bg-red-50"
          icon={<HiOutlineTrash className="w-4 h-4" />}
          onClick={() => onDelete(integration)}
          title="Delete Integration"
        >
          Delete
        </Button>
      </div>
    </Card>
  );
};

// Configuration Modal Component
const IntegrationModal = ({ isOpen, onClose, integration, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'custom_webhook',
    credentials: {},
    defaultValues: {
      status: 'new',
      priority: 'medium',
      stage: 'new_lead',
      source: '',
      autoAssign: false,
      assignTo: ''
    },
    assignmentRules: {
      enabled: false,
      strategy: 'round_robin',
      assignToUserIds: []
    },
    duplicateHandling: {
      enabled: true,
      matchBy: ['phone'],
      action: 'skip'
    },
    notifications: {
      onNewLead: true,
      notifyAdmins: true,
      notifyAssignedUser: true
    }
  });
  const [formErrors, setFormErrors] = useState({});
  const [webhookUrl, setWebhookUrl] = useState('');

  React.useEffect(() => {
    if (integration) {
      // Get credentials using the backend's key name (e.g., credentials.meta for meta_lead_ads type)
      const credentialKey = CREDENTIAL_KEY_MAP[integration.type] || integration.type;
      const typeCredentials = integration.credentials?.[credentialKey] || integration.credentials || {};

      // Handle masked credentials - backend sends e.g. { appIdMasked: '****1234' } with original key deleted
      // We show a sentinel value so the user knows a credential exists, and only send
      // changes to the backend (empty sentinel = don't update, new value = update)
      const MASKED_SENTINEL = '__MASKED__';
      const processedCredentials = {};
      Object.keys(typeCredentials).forEach(key => {
        if (key.endsWith('Masked')) {
          // e.g. appIdMasked -> appId, show sentinel to indicate value exists
          const originalKey = key.replace('Masked', '');
          processedCredentials[originalKey] = MASKED_SENTINEL;
        } else if (!key.endsWith('Masked')) {
          // Original key is present (unmasked, e.g. during create flow)
          processedCredentials[key] = typeCredentials[key] || '';
        }
      });

      setFormData({
        name: integration.name || '',
        type: integration.type || 'custom_webhook',
        credentials: processedCredentials,
        defaultValues: integration.defaultValues || {},
        assignmentRules: integration.assignmentRules || {},
        duplicateHandling: integration.duplicateHandling || {},
        notifications: integration.notifications || {}
      });
      // Use webhookUrl from API response
      setWebhookUrl(integration.webhookUrl || '');
    } else {
      setFormData({
        name: '',
        type: 'custom_webhook',
        credentials: {},
        defaultValues: {
          status: 'new',
          priority: 'medium',
          stage: 'new_lead',
          source: '',
          autoAssign: false,
          assignTo: ''
        },
        assignmentRules: {
          enabled: false,
          strategy: 'round_robin',
          assignToUserIds: []
        },
        duplicateHandling: {
          enabled: true,
          matchBy: ['phone'],
          action: 'skip'
        },
        notifications: {
          onNewLead: true,
          notifyAdmins: true,
          notifyAssignedUser: true
        }
      });
      setWebhookUrl('');
    }
    setFormErrors({});
  }, [integration, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Configuration name is required';
    }
    // Validate required credentials for new integrations
    if (!integration) {
      const typeConfig = INTEGRATION_TYPES.find(t => t.id === formData.type);
      if (typeConfig?.credentials?.length) {
        typeConfig.credentials.forEach(cred => {
          const value = formData.credentials[cred];
          if (!value || !value.trim()) {
            errors[`cred_${cred}`] = `${typeConfig.credentialsLabels[cred] || cred} is required`;
          }
        });
      }
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error(Object.values(errors)[0]);
      return;
    }
    setFormErrors({});
    onSave(formData);
  };

  const typeConfig = INTEGRATION_TYPES.find(t => t.id === formData.type);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={integration ? 'Edit Integration' : 'New Integration'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Settings */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Basic Settings</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Integration Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              disabled={!!integration}
            >
              {INTEGRATION_TYPES.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Configuration Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
              }}
              className={`w-full border rounded-md px-3 py-2 text-sm ${formErrors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500'}`}
              placeholder={`${typeConfig?.name || 'Integration'} Configuration`}
            />
            {formErrors.name && (
              <p className="text-xs text-red-600 font-medium mt-1">{formErrors.name}</p>
            )}
          </div>
        </div>

        {/* Webhook URL & Security */}
        {integration && webhookUrl && (
          <div className="space-y-2">
            <h4 className="font-medium text-gray-900">Webhook URL</h4>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={webhookUrl}
                readOnly
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                icon={<HiOutlineClipboardCopy className="w-4 h-4" />}
                onClick={() => copyToClipboard(webhookUrl)}
              >
                Copy
              </Button>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mt-2">
              <div className="flex items-start gap-2">
                <HiOutlineShieldCheck className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-amber-700">Signature Required</p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    All webhook requests must include an <code className="bg-amber-100 px-1 rounded">x-webhook-signature</code> header
                    with an HMAC-SHA256 signature generated using the integration's signing secret.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Credentials */}
        {typeConfig?.credentials && typeConfig.credentials.length > 0 && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">API Credentials</h4>
            {!integration && (
              <p className="text-xs text-gray-500">Credentials will be encrypted after saving.</p>
            )}
            {integration && (
              <p className="text-xs text-gray-500">Leave fields unchanged to keep existing values. Enter a new value to update.</p>
            )}
            {typeConfig.credentials.map(cred => {
              const currentValue = formData.credentials[cred] || '';
              const isMasked = currentValue === '__MASKED__';
              const credError = formErrors[`cred_${cred}`];
              return (
                <div key={cred}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {typeConfig.credentialsLabels[cred] || cred} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type={SECRET_CREDENTIALS.has(cred) ? 'password' : 'text'}
                    value={isMasked ? '' : currentValue}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        credentials: { ...formData.credentials, [cred]: e.target.value }
                      });
                      if (credError) setFormErrors({ ...formErrors, [`cred_${cred}`]: '' });
                    }}
                    className={`w-full border rounded-md px-3 py-2 text-sm ${credError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500'}`}
                    placeholder={isMasked ? '•••••••• (unchanged)' : `Enter ${typeConfig.credentialsLabels[cred] || cred}`}
                  />
                  {credError && (
                    <p className="text-xs text-red-600 font-medium mt-1">{credError}</p>
                  )}
                </div>
              );
            })}
            {/* Optional Credentials */}
            {typeConfig.optionalCredentials?.length > 0 && (
              <>
                <div className="border-t pt-3 mt-3">
                  <p className="text-xs text-gray-400 mb-2">Optional fields</p>
                </div>
                {typeConfig.optionalCredentials.map(cred => {
                  const currentValue = formData.credentials[cred] || '';
                  const isMasked = currentValue === '__MASKED__';
                  return (
                    <div key={cred}>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {typeConfig.credentialsLabels[cred] || cred} <span className="text-gray-400">(optional)</span>
                      </label>
                      <input
                        type={SECRET_CREDENTIALS.has(cred) ? 'password' : 'text'}
                        value={isMasked ? '' : currentValue}
                        onChange={(e) => setFormData({
                          ...formData,
                          credentials: { ...formData.credentials, [cred]: e.target.value }
                        })}
                        className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-50"
                        placeholder={isMasked ? '•••••••• (unchanged)' : `Enter ${typeConfig.credentialsLabels[cred] || cred}`}
                      />
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {/* Default Values */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Default Lead Values</h4>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Status
              </label>
              <select
                value={formData.defaultValues.status}
                onChange={(e) => setFormData({
                  ...formData,
                  defaultValues: { ...formData.defaultValues, status: e.target.value }
                })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                {/* <option value="new">New</option> */}
                {/* <option value="open">Open</option> */}
                {/* <option value="in_progress">In Progress</option> */}
                {/* <option value="follow_up">Follow Up</option> */}
                <option value="cold">Cold</option>
                <option value="warm">Warm</option>
                <option value="hot">Hot</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={formData.defaultValues.priority}
                onChange={(e) => setFormData({
                  ...formData,
                  defaultValues: { ...formData.defaultValues, priority: e.target.value }
                })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lead Source
              </label>
              <input
                type="text"
                value={formData.defaultValues.source || typeConfig?.name || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  defaultValues: { ...formData.defaultValues, source: e.target.value }
                })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder={typeConfig?.name || 'Source'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Auto Assign
              </label>
              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  checked={formData.defaultValues.autoAssign}
                  onChange={(e) => setFormData({
                    ...formData,
                    defaultValues: { ...formData.defaultValues, autoAssign: e.target.checked }
                  })}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Automatically assign leads</span>
              </div>
            </div>
          </div>
        </div>

        {/* Duplicate Handling */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Duplicate Handling</h4>

          <div className="flex items-center gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.duplicateHandling.enabled}
                onChange={(e) => setFormData({
                  ...formData,
                  duplicateHandling: { ...formData.duplicateHandling, enabled: e.target.checked }
                })}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Enable duplicate detection</span>
            </label>
          </div>

          {formData.duplicateHandling.enabled && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Match By
                </label>
                <select
                  value={formData.duplicateHandling.matchBy[0] || 'phone'}
                  onChange={(e) => setFormData({
                    ...formData,
                    duplicateHandling: { ...formData.duplicateHandling, matchBy: [e.target.value] }
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="phone">Phone Number</option>
                  <option value="email">Email Address</option>
                  <option value="phone_email">Phone or Email</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Action on Duplicate
                </label>
                <select
                  value={formData.duplicateHandling.action}
                  onChange={(e) => setFormData({
                    ...formData,
                    duplicateHandling: { ...formData.duplicateHandling, action: e.target.value }
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="skip">Skip (do not import)</option>
                  <option value="update">Update existing lead</option>
                  <option value="create_duplicate">Create duplicate</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">
            {integration ? 'Save Changes' : 'Create Integration'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// Logs Modal Component
const LogsModal = ({ isOpen, onClose, integration }) => {
  const [filters, setFilters] = useState({ status: '', page: 1 });

  const { data, isLoading } = useQuery({
    queryKey: ['integrationLogs', integration?._id, filters],
    queryFn: async () => {
      if (!integration) return null;
      const response = await integrationsAPI.getLogs(integration._id, filters);
      return response.data;
    },
    enabled: isOpen && !!integration
  });

  const statusStyles = {
    success: 'bg-green-100 text-green-700',
    partial_success: 'bg-yellow-100 text-yellow-700',
    failed: 'bg-red-100 text-red-700',
    duplicate: 'bg-blue-100 text-blue-700',
    error: 'bg-red-100 text-red-700'
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Logs - ${integration?.name || ''}`} size="xl">
      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
        >
          <option value="">All Status</option>
          <option value="success">Success</option>
          <option value="partial_success">Partial Success</option>
          <option value="failed">Failed</option>
          <option value="duplicate">Duplicate</option>
          <option value="error">Error</option>
        </select>

        <div className="flex-1" />

        <div className="text-sm text-gray-500">
          Total: {data?.pagination?.total || 0} logs
        </div>
      </div>

      {/* Logs Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : !data?.data?.length ? (
        <div className="text-center py-8 text-gray-500">
          No logs found
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 px-3">Date/Time</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Received</th>
                <th className="py-2 px-3">Imported</th>
                <th className="py-2 px-3">Duplicates</th>
                <th className="py-2 px-3">Errors</th>
                <th className="py-2 px-3">Duration</th>
                <th className="py-2 px-3">Details</th>
              </tr>
            </thead>
            <tbody>
              {data?.data?.map((log, index) => (
                <tr key={log._id || index} className="border-b">
                  <td className="py-2 px-3 whitespace-nowrap">
                    {new Date(log.createdAt).toLocaleString()}
                  </td>
                  <td className="py-2 px-3">
                    <Badge className={statusStyles[log.status] || 'bg-gray-100 text-gray-700'}>
                      {log.status}
                    </Badge>
                  </td>
                  <td className="py-2 px-3">{log.leads?.received || 0}</td>
                  <td className="py-2 px-3 text-green-600">{log.leads?.imported || 0}</td>
                  <td className="py-2 px-3 text-blue-600">{log.leads?.duplicates || 0}</td>
                  <td className="py-2 px-3 text-red-600">{log.leads?.errors || 0}</td>
                  <td className="py-2 px-3">{log.processing?.durationMs || 0}ms</td>
                  <td className="py-2 px-3">
                    {log.error?.message ? (
                      <span className="text-red-600 truncate block max-w-xs" title={log.error.message}>
                        {log.error.message}
                      </span>
                    ) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {data?.pagination?.pages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={filters.page <= 1}
            onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
          >
            Previous
          </Button>
          <span className="px-4 py-2 text-sm">
            Page {filters.page} of {data.pagination.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={filters.page >= data.pagination.pages}
            onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
          >
            Next
          </Button>
        </div>
      )}
    </Modal>
  );
};

// Delete Confirmation Modal Component
const DeleteModal = ({ isOpen, onClose, integration, onConfirm, isLoading }) => {
  if (!integration) return null;

  const typeConfig = INTEGRATION_TYPES.find(t => t.id === integration.type) || INTEGRATION_TYPES[3];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Integration" size="md">
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <HiOutlineExclamation className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h4 className="font-medium text-gray-900">Delete {integration.name || typeConfig.name}?</h4>
            <p className="text-sm text-gray-500 mt-1">
              This action cannot be undone. All configuration, logs, and webhook data for this integration will be permanently deleted.
            </p>
          </div>
        </div>

        {integration.stats?.totalImported > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-700">
              <strong>Warning:</strong> This integration has imported {integration.stats.totalImported} lead(s). Deleting it will not remove the imported leads, but the webhook endpoint will stop accepting new leads.
            </p>
          </div>
        )}

        {integration.isActive && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-700">
              <strong>Warning:</strong> This integration is currently active. Deleting it will immediately stop all incoming leads from {typeConfig.name}.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Delete Integration'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Main Component
const Integrations = () => {
  const { isAuthenticated, isInitialized } = useAuth();
  const queryClient = useQueryClient();
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [logsIntegration, setLogsIntegration] = useState(null);
  const [deleteIntegration, setDeleteIntegration] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Fetch integrations
  const { data: integrations, isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      const response = await integrationsAPI.getAll();
      return response.data?.data || [];
    },
    enabled: isAuthenticated && isInitialized
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => integrationsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['integrations']);
      toast.success('Integration created! Click the eye icon on the signing secret to view it — it can only be revealed once.', { duration: 8000 });
      setIsConfigModalOpen(false);
      setSelectedIntegration(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create integration');
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => integrationsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['integrations']);
      toast.success('Integration updated successfully');
      setIsConfigModalOpen(false);
      setSelectedIntegration(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update integration');
    }
  });

  // Toggle mutation
  const toggleMutation = useMutation({
    mutationFn: ({ id, enable }) =>
      enable ? integrationsAPI.enable(id) : integrationsAPI.disable(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['integrations']);

      // Show warning if credentials not configured
      if (response.data?.warning) {
        toast.error(response.data.message, { duration: 6000 });
      } else {
        toast.success(response.data?.message || 'Integration updated');
      }
    },
    onError: (error) => {
      const message = error.response?.data?.message || 'Failed to update integration';
      toast.error(message);
    }
  });

  // Test mutation
  const testMutation = useMutation({
    mutationFn: (id) => integrationsAPI.test(id),
    onSuccess: (response) => {
      if (response.data?.success) {
        toast.success('Connection test successful');
      } else {
        toast.error(response.data?.message || 'Connection test failed');
      }
      queryClient.invalidateQueries(['integrations']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Connection test failed');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => integrationsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['integrations']);
      toast.success('Integration deleted successfully');
      setIsDeleteModalOpen(false);
      setDeleteIntegration(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete integration');
    }
  });

  const handleConfigure = (integration) => {
    setSelectedIntegration(integration);
    setIsConfigModalOpen(true);
  };

  const handleToggle = (integration, enable) => {
    const isActivation = integration.status === 'pending_verification' && enable;
    const message = isActivation
      ? 'Activate this integration? This will allow it to start receiving leads via webhook.'
      : `Are you sure you want to ${enable ? 'enable' : 'disable'} this integration?`;

    if (window.confirm(message)) {
      toggleMutation.mutate({ id: integration._id, enable });
    }
  };

  const handleViewLogs = (integration) => {
    setLogsIntegration(integration);
    setIsLogsModalOpen(true);
  };

  const handleTest = (integration) => {
    testMutation.mutate(integration._id);
  };

  const handleDelete = (integration) => {
    setDeleteIntegration(integration);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (deleteIntegration) {
      deleteMutation.mutate(deleteIntegration._id);
    }
  };

  const handleSave = (formData) => {
    // Nest credentials under the backend's expected key name (e.g., { meta: { appId, appSecret } } not { meta_lead_ads: { ... } })
    const typeConfig = INTEGRATION_TYPES.find(t => t.id === formData.type);
    const credentialKey = CREDENTIAL_KEY_MAP[formData.type] || formData.type;

    // Strip out __MASKED__ sentinel values — only send credentials the user actually changed
    const allCredFields = [...(typeConfig?.credentials || []), ...(typeConfig?.optionalCredentials || [])];
    const cleanCredentials = {};
    if (formData.credentials) {
      Object.keys(formData.credentials).forEach(key => {
        const value = formData.credentials[key];
        // Skip sentinel (unchanged) values — backend will keep the existing value
        if (value && value !== '__MASKED__') {
          cleanCredentials[key] = value;
        }
      });
    }

    // Only nest if there are credential fields for this type and some credentials to send
    const nestedCredentials = (typeConfig?.credentials?.length || typeConfig?.optionalCredentials?.length) && Object.keys(cleanCredentials).length > 0
      ? { [credentialKey]: cleanCredentials }
      : undefined; // Don't send credentials object if nothing changed

    const dataToSave = {
      ...formData,
      credentials: nestedCredentials
    };

    if (selectedIntegration) {
      updateMutation.mutate({ id: selectedIntegration._id, data: dataToSave });
    } else {
      createMutation.mutate(dataToSave);
    }
  };

  if (!isAuthenticated || !isInitialized) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Integrations</h3>
          <p className="text-gray-500 text-sm mt-1">
            Connect third-party platforms to automatically import leads. All webhooks are secured with HMAC-SHA256 signature verification.
          </p>
        </div>
        <Button
          icon={<HiOutlinePlus className="w-4 h-4" />}
          onClick={() => {
            setSelectedIntegration(null);
            setIsConfigModalOpen(true);
          }}
        >
          Add Integration
        </Button>
      </div>

      {/* Integration Cards */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : !integrations?.length ? (
        <Card padding="p-8">
          <div className="text-center">
            <HiOutlineLink className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No integrations configured</h3>
            <p className="text-gray-500 mb-4">
              Connect your lead sources to automatically import leads into your CRM
            </p>
            <Button
              icon={<HiOutlinePlus className="w-4 h-4" />}
              onClick={() => {
                setSelectedIntegration(null);
                setIsConfigModalOpen(true);
              }}
            >
              Add Your First Integration
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {integrations.map(integration => (
            <IntegrationCard
              key={integration._id}
              integration={integration}
              onConfigure={handleConfigure}
              onToggle={handleToggle}
              onViewLogs={handleViewLogs}
              onTest={handleTest}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Configuration Modal */}
      <IntegrationModal
        isOpen={isConfigModalOpen}
        onClose={() => {
          setIsConfigModalOpen(false);
          setSelectedIntegration(null);
        }}
        integration={selectedIntegration}
        onSave={handleSave}
      />

      {/* Logs Modal */}
      <LogsModal
        isOpen={isLogsModalOpen}
        onClose={() => {
          setIsLogsModalOpen(false);
          setLogsIntegration(null);
        }}
        integration={logsIntegration}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteIntegration(null);
        }}
        integration={deleteIntegration}
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

export default Integrations;